import {
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { createItem, updateItem, ItemRequestStatus } from "../../api/generics";
import Supplier, { newSupplier } from "../../models/supplier";
import Errors, { getFieldErrors } from "../../models/errors";
import { SUCCESS } from "../../utils/constants/tags";
import { FieldErrors } from "../form/FieldErrors";
import { errorAlert } from "../utils/messages";
import { emptyValueOnUndefined } from "../../utils/fields";
import { RootState } from "../../store";
import { useSelector } from "react-redux";

const FormLabel: React.FC<{ required?: boolean; children: React.ReactNode }> = ({ required, children }) => (
  <CLabel className="mb-0" style={{ fontWeight: 600, fontSize: "0.875rem", color: "var(--theme-text-soft)" }}>
    {required && <span className="text-danger mr-1">*</span>}
    {children}
  </CLabel>
);

interface UserFormProps {
  initialSupplier?: Supplier;
  initialErrors?: Errors;
  onCancel: () => void | Promise<void>;
  onSuccess: () => void | Promise<void>;
}

const SupplierForm: React.FC<UserFormProps> = ({
  initialSupplier,
  initialErrors,
  onCancel,
  onSuccess,
}) => {
  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer
  );
  const [editingSupplier, setEditingSupplier] = useState<Supplier>(
    initialSupplier ? initialSupplier : newSupplier()
  );

  const [errors, setErrors] = useState<Errors>(
    initialErrors ? initialErrors : {}
  );
  const [submitting, setSubmitting] = useState(false);

  const onFantasyNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingSupplier({
      ...editingSupplier,
      fantasyName: e.target.value,
    });
  };

  const onLegalNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingSupplier({
      ...editingSupplier,
      legalName: e.target.value,
    });
  };

  const onRucChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingSupplier({
      ...editingSupplier,
      ruc: e.target.value,
    });
  };

  const onSave = async () => {
    setSubmitting(true);
    let toSendSupplier = {
      ...editingSupplier,
      taxPayerId: taxPayer.id,
    };
    let requestPromise: Promise<ItemRequestStatus<Supplier>>;
    if (editingSupplier.id === undefined) {
      requestPromise = createItem<Supplier>(
        "/suppliers/create/",
        toSendSupplier
      );
    } else {
      requestPromise = updateItem<Supplier>(
        `/suppliers/${toSendSupplier.id}/`,
        toSendSupplier
      );
    }

    const requestStatus = await requestPromise;

    if (requestStatus.status !== SUCCESS) {
      if (requestStatus.errors !== undefined) {
        setErrors(requestStatus.errors);
      }

      let message = "Ha ocurrido un error!!";
      if (requestStatus.detail !== undefined) {
        message = requestStatus.detail;
      }
      errorAlert(message);
    } else {
      setErrors({});
      clearForm();
      onSuccess();
    }
    setSubmitting(false);
  };

  const onClose = () => {
    clearForm();
    onCancel();
  };

  const clearForm = () => {
    setEditingSupplier(newSupplier());
  };

  useEffect(() => {
    setEditingSupplier(initialSupplier ? initialSupplier : newSupplier());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSupplier]);

  useEffect(() => {
    setErrors(initialErrors ? initialErrors : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialErrors]);

  return (
    <>
      <fieldset disabled={submitting}>
        <CFormGroup>
          <CRow className="align-items-center">
            <CCol md={3}>
              <FormLabel required>Razón Social:</FormLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                value={emptyValueOnUndefined(editingSupplier.legalName)}
                onChange={onLegalNameChange}
                placeholder="Nombre legal"
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("legalName", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow className="align-items-center">
            <CCol md={3}>
              <FormLabel required>RUC:</FormLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                value={emptyValueOnUndefined(editingSupplier.ruc)}
                onChange={onRucChange}
                placeholder="RUC"
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("ruc", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow className="align-items-center">
            <CCol md={3}>
              <FormLabel>Nombre de Fantasía:</FormLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                value={emptyValueOnUndefined(editingSupplier.fantasyName)}
                onChange={onFantasyNameChange}
                placeholder="Nombre de Fantasía"
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("fantasyName", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup className="float-right">
          <CButtonGroup>
            <CButton
              type="button"
              color="secondary"
              onClick={onClose}
              style={{ fontWeight: 600 }}
            >
              <i className="fa fa-times mr-2"></i>
              Atrás
            </CButton>
            <CButton
              type="submit"
              color="primary"
              onClick={onSave}
              style={{ fontWeight: 600, minWidth: 120 }}
            >
              {submitting ? (
                <Spinner
                  animation="grow"
                  style={{
                    height: "17px",
                    width: "17px",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginRight: "10px",
                  }}
                />
              ) : (
                <i className="fa fa-save mr-2"></i>
              )}
              {submitting ? "Guardando..." : "Guardar"}
            </CButton>
          </CButtonGroup>
        </CFormGroup>
      </fieldset>
    </>
  );
};

export default SupplierForm;
